import {useEffect, useMemo, useState} from "react";
import axios from "axios";
import {loadStripe} from "@stripe/stripe-js/pure";
import {CardElement, Elements, useElements, useStripe} from "@stripe/react-stripe-js";
import WebsiteCardElement from "../../components/website-card-element";

const createIdempotencyKey = () => (
    typeof window !== "undefined" && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`
);

const getErrorMessage = (error, fallback) => {
    const responseMessage = error?.response?.data;
    return typeof responseMessage === "string" && responseMessage.trim()
        ? responseMessage
        : fallback;
};

function CardSetupForm({customerId, config, onSaved, onCancel}) {
    const stripe = useStripe();
    const elements = useElements();
    const [cardComplete, setCardComplete] = useState(false);
    const [cardError, setCardError] = useState("");
    const [consentAccepted, setConsentAccepted] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const saveCard = async (event) => {
        event.preventDefault();
        if (saving) return;
        setError("");
        setCardError("");

        if (!stripe || !elements) {
            setError("Secure card entry is still loading. Please try again.");
            return;
        }
        if (!cardComplete) {
            setCardError("Enter your card details to continue.");
            return;
        }
        if (!consentAccepted) {
            setError("Please confirm that this card can be saved.");
            return;
        }

        const card = elements.getElement(CardElement);
        if (!card) {
            setError("Secure card entry is unavailable. Please try again.");
            return;
        }

        setSaving(true);
        try {
            const {data: operation} = await axios.post(
                `/api/v2/customers/${customerId}/payment-methods/setup-intent`,
                {
                    idempotencyKey: createIdempotencyKey(),
                    consentAccepted: true,
                    consentVersion: config.consentVersion
                }
            );
            if (!operation?.clientSecret) throw new Error("The card setup could not be started.");

            const confirmation = await stripe.confirmCardSetup(operation.clientSecret, {
                payment_method: {card}
            });
            if (confirmation?.error) throw confirmation.error;
            if (!confirmation?.setupIntent?.id) throw new Error("The card setup could not be confirmed.");

            const {data: method} = await axios.post(
                `/api/v2/customers/${customerId}/payment-methods/complete`,
                {setupIntentReference: confirmation.setupIntent.id}
            );
            onSaved(method);
        } catch (saveError) {
            setError(getErrorMessage(
                saveError,
                saveError?.message || "The card could not be saved. Check the details or try another card."
            ));
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={saveCard}>
            <div className="mb-4">
                <h4 className="!text-base !leading-snug font-bold !mb-0">Add a Card</h4>
                <p className="mt-1 !mb-0 text-sm text-gray-700">
                    Your card details are stored securely by our payment provider.
                </p>
            </div>
            <div className="max-w-2xl space-y-4">
                <WebsiteCardElement
                    id="account-payment-card"
                    label="Card Details"
                    required
                    error={cardError}
                    options={{hidePostalCode: true, disableLink: true}}
                    onChange={(event) => {
                        setCardComplete(event.complete === true);
                        if (event.error) setCardError(event.error.message || "Check your card details.");
                        else if (cardError) setCardError("");
                    }}
                />
                <label className="flex items-start gap-3 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={consentAccepted}
                        onChange={(event) => setConsentAccepted(event.target.checked)}
                        className="mt-1"
                    />
                    <span>{config.consentText}</span>
                </label>
                {error ? <p role="alert" className="text-sm text-red-600 mb-0">{error}</p> : null}
                <div className="flex flex-wrap items-center gap-4 pt-1">
                    <button type="button" className="text-link font-semibold hover:opacity-80 disabled:opacity-50" onClick={onCancel} disabled={saving}>
                        Cancel
                    </button>
                    <button type="submit" className="button w-fit" disabled={saving}>
                        {saving ? "Saving…" : "Save Card"}
                    </button>
                </div>
            </div>
        </form>
    );
}

export default function AccountPaymentMethodForm({customerId, onSaved, onCancel}) {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError("");
        axios.get(`/api/v2/customers/${customerId}/payment-methods/setup-config`)
            .then(({data}) => {
                if (!cancelled) setConfig(data);
            })
            .catch((requestError) => {
                if (!cancelled) setError(getErrorMessage(
                    requestError,
                    "Card saving is not available right now. Please try again later."
                ));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [customerId]);

    const stripePromise = useMemo(() => {
        if (!config?.publishableKey || !config?.stripeAccountId) return null;
        return loadStripe(config.publishableKey, {stripeAccount: config.stripeAccountId});
    }, [config]);

    if (loading) return <p role="status" className="text-sm text-gray-700">Loading secure card entry…</p>;
    if (error || !config || !stripePromise) {
        return (
            <div role="alert" className="rounded-theme bg-red-50 px-4 py-3 text-sm text-red-800">
                {error || "Card saving is not available right now. Please try again later."}
            </div>
        );
    }

    return (
        <Elements stripe={stripePromise}>
            <CardSetupForm
                customerId={customerId}
                config={config}
                onSaved={onSaved}
                onCancel={onCancel}
            />
        </Elements>
    );
}
